import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { INNHypeVideoModal, INNHypeVideoThumb } from '../components/INNHypeVideoModal';

// ── Data ──────────────────────────────────────────────
const QUESTIONS = [
  {
    id: 'q1',
    text: 'Be honest \u2014 how are you tracking your deals and commissions?',
    options: [
      { emoji: '\uD83D\uDCCA', label: "A spreadsheet I'm emotionally attached to", value: 'spreadsheets' },
      { emoji: '\uD83D\uDCDD', label: 'Napkins, sticky notes, and vibes', value: 'paper' },
      { emoji: '\uD83D\uDCBB', label: 'A CRM I actually use (flex)', value: 'crm' },
      { emoji: '\uD83E\uDD37', label: 'Track? I just check my bank account and pray', value: 'nothing' },
    ],
  },
  {
    id: 'q2',
    text: 'How many deals are you closing per year?',
    options: [
      { emoji: '\uD83C\uDFE0', label: '1 - 5 (just getting warmed up)', value: '1-5' },
      { emoji: '\uD83C\uDFD8\uFE0F', label: '6 - 15 (keeping the lights on)', value: '6-15' },
      { emoji: '\uD83C\uDFD9\uFE0F', label: "16 - 30 (now we're cooking)", value: '16-30' },
      { emoji: '\uD83D\uDE80', label: "30+ (I don't sleep)", value: '30+' },
    ],
  },
  {
    id: 'q3',
    text: 'What keeps you up at 2 AM?',
    options: [
      { emoji: '\uD83D\uDCB0', label: 'I made money... I think? Where did it go?', value: 'money' },
      { emoji: '\u23F0', label: 'A due diligence deadline I definitely forgot', value: 'deadlines' },
      { emoji: '\uD83D\uDCC9', label: "The prospecting I promised myself I'd do Monday", value: 'consistency' },
      { emoji: '\uD83D\uDD25', label: 'Yes. All of it. Everything. Send help.', value: 'everything' },
    ],
  },
  {
    id: 'q4',
    text: 'How much of your week gets eaten by admin and deal tracking?',
    options: [
      { emoji: '\u26A1', label: "Under an hour \u2014 I'm built different", value: 'under1' },
      { emoji: '\uD83D\uDD50', label: '1 - 3 hours (manageable)', value: '1-3' },
      { emoji: '\uD83D\uDD53', label: "3 - 5 hours of my life I'll never get back", value: '3-5' },
      { emoji: '\uD83D\uDE29', label: "5+ hours \u2014 I didn't become an agent for this", value: '5+' },
    ],
  },
  {
    id: 'q5',
    text: 'After splits, fees, and expenses \u2014 do you know what you actually kept last year?',
    options: [
      { emoji: '\u2705', label: "To the penny. I'm that person.", value: 'yes' },
      { emoji: '\uD83E\uDD0F', label: "Ballpark? Sure. Exact number? Don't ask.", value: 'roughly' },
      { emoji: '\uD83D\uDE2C', label: 'I know what my gross was... that counts right?', value: 'not-really' },
      { emoji: '\uD83D\uDE48', label: "I'd rather eat a cold open house hot dog", value: 'no-idea' },
    ],
  },
];

const SCORE_MAP = {
  q1: { nothing: -20, paper: -10, spreadsheets: -5, crm: 5 },
  q2: { '1-5': -5, '6-15': 0, '16-30': 5, '30+': 10 },
  q3: { everything: -15, money: -10, deadlines: -10, consistency: -5 },
  q4: { '5+': -10, '3-5': -5, '1-3': 0, under1: 5 },
  q5: { yes: 15, roughly: 0, 'not-really': -10, 'no-idea': -15 },
};

const RESULTS = [
  {
    min: 70,
    type: 'The Spreadsheet Whisperer',
    badge: { emoji: '\uD83C\uDFC6', label: 'Gold Star' },
    description: "You're more organized than 90% of agents out there. Honestly impressive. But even you've got money leaking through cracks you can't see \u2014 and at your volume, those cracks are expensive.",
    insight: "At your level, a 2% improvement in tracking splits and fees could mean an extra $5,000-$10,000 per year. You're already doing the hard part \u2014 the right tool just turns 'good' into 'give me my money.'",
  },
  {
    min: 45,
    type: 'The Professional Wing-It Artist',
    badge: { emoji: '\uD83C\uDF96\uFE0F', label: 'Points for Style' },
    description: "You're closing deals and making money \u2014 but if someone asked you exactly how much you kept after splits, fees, and that closing dinner you expensed... you'd change the subject.",
    insight: "Agents in your zone typically discover they've been undercharging, missing fee deductions, or losing track of 1-2 deals per quarter. The money is there. You're just not collecting all of it.",
  },
  {
    min: 0,
    type: 'The Vibes-Based Business Model',
    badge: { emoji: '\uD83C\uDFC5', label: 'A for Effort' },
    description: "You work hard, you hustle, you close \u2014 and then your commission disappears into some combination of splits, fees, gas, and that one staging expense you forgot about. Sound familiar?",
    insight: "Agents in your situation are typically leaving 15-20% of their income on the table because they literally can't see their own numbers. The good news? The fix takes about 5 minutes.",
  },
];

const BENEFITS = [
  { emoji: '\uD83D\uDCB0', title: 'Know your real numbers', desc: 'See exactly what you keep after splits, fees, and expenses \u2014 no more guessing, no more surprises at tax time.' },
  { emoji: '\u23F0', title: 'Never miss a deadline', desc: 'Get automatic reminders before due diligence dates so you stop waking up in a cold sweat.' },
  { emoji: '\uD83D\uDCCA', title: 'Track every deal in one place', desc: 'From contract to close, see where every deal stands without digging through emails and spreadsheets.' },
  { emoji: '\uD83C\uDFE6', title: 'Built for agents, not accountants', desc: "No complicated setup. No learning curve. If you can close a deal, you can use this app." },
];

const FORM_ID = '1FAIpQLSfjCwmfaPvQhUwWshxN3CwMgDid5wP1fiVoF697ZsDKglxaQg';
const ENTRY_IDS = {
  name: 'entry.1546318377',
  email: 'entry.695417342',
  q1: 'entry.1352535853',
  q2: 'entry.2022949002',
  q3: 'entry.490593816',
  q4: 'entry.564609618',
  q5: 'entry.1244590146',
  score: 'entry.759719585',
  resultType: 'entry.1615806026',
};

// ── Styles ────────────────────────────────────────────
const S = {
  page: {
    minHeight: '100dvh',
    background: '#0a0a0a',
    color: '#fff',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0 16px',
    overflowX: 'hidden',
  },
  container: {
    width: '100%',
    maxWidth: 440,
    margin: '0 auto',
    padding: '40px 0 60px',
    position: 'relative',
  },
  fadeIn: {
    animation: 'quizFadeIn 0.3s ease both',
  },
  badge: {
    display: 'inline-block',
    background: 'rgba(108,92,231,0.15)',
    color: '#a78bfa',
    fontSize: 12,
    fontWeight: 600,
    padding: '6px 14px',
    borderRadius: 20,
    letterSpacing: 0.5,
    marginBottom: 20,
  },
  h1: {
    fontSize: 28,
    fontWeight: 800,
    lineHeight: 1.25,
    marginBottom: 16,
  },
  subtitle: {
    fontSize: 15,
    color: '#9ca3af',
    lineHeight: 1.6,
    marginBottom: 28,
  },
  social: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 32,
  },
  avatarRow: {
    display: 'flex',
  },
  avatar: (i) => ({
    width: 30,
    height: 30,
    borderRadius: '50%',
    background: ['#6c5ce7', '#a855f7', '#7c3aed', '#8b5cf6'][i],
    border: '2px solid #0a0a0a',
    marginLeft: i > 0 ? -8 : 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 700,
    color: '#fff',
  }),
  socialText: {
    fontSize: 13,
    color: '#9ca3af',
  },
  btn: {
    width: '100%',
    padding: '18px 24px',
    background: 'linear-gradient(135deg, #6c5ce7, #a855f7)',
    color: '#fff',
    border: 'none',
    borderRadius: 14,
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    marginBottom: 12,
    transition: 'transform 0.15s, opacity 0.15s',
  },
  btnHover: {
    transform: 'scale(0.98)',
    opacity: 0.9,
  },
  smallText: {
    fontSize: 12,
    color: '#6b7280',
    textAlign: 'center',
  },
  progressWrap: {
    width: '100%',
    height: 4,
    background: '#1e1e36',
    borderRadius: 2,
    marginBottom: 32,
    overflow: 'hidden',
  },
  progressFill: (pct) => ({
    width: `${pct}%`,
    height: '100%',
    background: 'linear-gradient(90deg, #6c5ce7, #a855f7)',
    borderRadius: 2,
    transition: 'width 0.4s ease',
  }),
  qNum: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: 600,
    marginBottom: 8,
    letterSpacing: 1,
  },
  qText: {
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1.35,
    marginBottom: 24,
  },
  option: (selected) => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '16px 18px',
    background: selected ? 'rgba(108,92,231,0.2)' : '#141422',
    border: `1.5px solid ${selected ? '#6c5ce7' : '#1e1e36'}`,
    borderRadius: 14,
    color: '#fff',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    marginBottom: 10,
    textAlign: 'left',
    transition: 'all 0.15s ease',
    lineHeight: 1.4,
  }),
  optionEmoji: {
    fontSize: 20,
    flexShrink: 0,
  },
  input: {
    width: '100%',
    padding: '16px 18px',
    background: '#141422',
    border: '1.5px solid #1e1e36',
    borderRadius: 14,
    color: '#fff',
    fontSize: 15,
    fontWeight: 500,
    marginBottom: 12,
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  skipLink: {
    display: 'block',
    textAlign: 'center',
    color: '#6b7280',
    fontSize: 13,
    cursor: 'pointer',
    marginTop: 8,
    background: 'none',
    border: 'none',
    textDecoration: 'underline',
    fontFamily: 'inherit',
  },
  videoLinkBtn: {
    display: 'block',
    width: '100%',
    minHeight: 44,
    textAlign: 'center',
    color: '#a78bfa',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    marginTop: 12,
    padding: '10px 16px',
    background: 'transparent',
    border: '1px solid rgba(167, 139, 250, 0.3)',
    borderRadius: 10,
    fontFamily: 'inherit',
    transition: 'border-color 160ms ease, color 160ms ease',
  },
  scoreCircle: {
    width: 120,
    height: 120,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #6c5ce7, #a855f7)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 14px',
    padding: 8,
    boxSizing: 'border-box',
    textAlign: 'center',
  },
  badgeEmoji: {
    fontSize: 44,
    lineHeight: 1,
  },
  badgeLabel: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    marginTop: 6,
    opacity: 0.95,
    lineHeight: 1.1,
  },
  scoreReadout: {
    textAlign: 'center',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    color: '#9ca3af',
    marginBottom: 14,
  },
  scoreReadoutNum: {
    color: '#fff',
    fontWeight: 800,
  },
  resultType: {
    fontSize: 22,
    fontWeight: 800,
    textAlign: 'center',
    marginBottom: 16,
    background: 'linear-gradient(135deg, #6c5ce7, #a855f7)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  resultDesc: {
    fontSize: 14,
    color: '#9ca3af',
    lineHeight: 1.65,
    textAlign: 'center',
    marginBottom: 28,
  },
  insightCard: {
    background: '#141422',
    border: '1.5px solid #1e1e36',
    borderRadius: 14,
    padding: 20,
    marginBottom: 28,
  },
  insightTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#a78bfa',
    marginBottom: 8,
  },
  insightText: {
    fontSize: 13,
    color: '#9ca3af',
    lineHeight: 1.6,
  },
  benefitItem: {
    display: 'flex',
    gap: 16,
    marginBottom: 24,
    alignItems: 'flex-start',
  },
  benefitEmoji: {
    fontSize: 28,
    flexShrink: 0,
    marginTop: 2,
  },
  benefitTitle: {
    fontSize: 15,
    fontWeight: 700,
    marginBottom: 4,
  },
  benefitDesc: {
    fontSize: 13,
    color: '#9ca3af',
    lineHeight: 1.55,
  },
  errorText: {
    color: '#f87171',
    fontSize: 12,
    marginBottom: 8,
    marginTop: -8,
  },
};

// ── Component ─────────────────────────────────────────
const QuizPage = () => {
  const [screen, setScreen] = useState(0); // 0=intro, 1-5=questions, 6=email, 7=results, 8=benefits
  const [answers, setAnswers] = useState({});
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [animKey, setAnimKey] = useState(0);
  const iframeRef = useRef(null);
  const [videoOpen, setVideoOpen] = useState(false);

  const goTo = useCallback((s) => {
    setAnimKey((k) => k + 1);
    setSelected(null);
    setScreen(s);
  }, []);

  const handleAnswer = useCallback((qIdx, value) => {
    setSelected(value);
    setAnswers((prev) => ({ ...prev, [`q${qIdx}`]: value }));
    setTimeout(() => {
      if (qIdx < 5) goTo(qIdx + 1);
      else goTo(6);
    }, 300);
  }, [goTo]);

  const calcScore = useCallback(() => {
    let s = 50;
    for (let i = 1; i <= 5; i++) {
      const a = answers[`q${i}`];
      if (a && SCORE_MAP[`q${i}`]) s += SCORE_MAP[`q${i}`][a] || 0;
    }
    return Math.max(15, Math.min(95, s));
  }, [answers]);

  const getResult = useCallback((score) => {
    return RESULTS.find((r) => score >= r.min) || RESULTS[RESULTS.length - 1];
  }, []);

  const submitToGoogle = useCallback((score, resultType) => {
    const iframe = document.createElement('iframe');
    iframe.name = 'quiz_submit_frame';
    iframe.style.display = 'none';
    document.body.appendChild(iframe);

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `https://docs.google.com/forms/d/e/${FORM_ID}/formResponse`;
    form.target = 'quiz_submit_frame';

    const fields = {
      [ENTRY_IDS.name]: name,
      [ENTRY_IDS.email]: email,
      [ENTRY_IDS.q1]: answers.q1 || '',
      [ENTRY_IDS.q2]: answers.q2 || '',
      [ENTRY_IDS.q3]: answers.q3 || '',
      [ENTRY_IDS.q4]: answers.q4 || '',
      [ENTRY_IDS.q5]: answers.q5 || '',
      [ENTRY_IDS.score]: String(score),
      [ENTRY_IDS.resultType]: resultType,
    };

    Object.entries(fields).forEach(([fieldName, fieldValue]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = fieldName;
      input.value = fieldValue;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    setTimeout(() => {
      form.remove();
      iframe.remove();
    }, 5000);
  }, [name, email, answers]);

  const handleEmailSubmit = useCallback(() => {
    if (!email.includes('@')) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError('');
    const score = calcScore();
    const result = getResult(score);
    submitToGoogle(score, result.type);
    goTo(7);
  }, [email, calcScore, getResult, submitToGoogle, goTo]);

  const handleSkip = useCallback(() => goTo(7), [goTo]);

  const score = calcScore();
  const result = getResult(score);
  const qIdx = screen >= 1 && screen <= 5 ? screen : null;

  return (
    <div style={S.page} data-testid="quiz-page">
      <Helmet>
        <title>Real Estate Agent Assessment | I Need Numbers</title>
        <meta name="description" content="Take the 60-second assessment and discover what's holding your real estate business back. Powered by I Need Numbers." />
        <meta property="og:title" content="What's Your Real Estate Business Score? | I Need Numbers" />
        <meta property="og:description" content="Take the 60-second assessment. Trusted by agents closing 6-figures+." />
        <meta property="og:type" content="website" />
      </Helmet>

      <style>{`
        @keyframes quizFadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div style={S.container}>
        {/* Progress bar - questions only */}
        {qIdx && (
          <div style={S.progressWrap}>
            <div style={S.progressFill((qIdx / 5) * 100)} />
          </div>
        )}

        {/* ── Screen 0: Intro ── */}
        {screen === 0 && (
          <div key={animKey} style={S.fadeIn}>
            <div style={{ textAlign: 'center', paddingTop: 40 }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_agent-portal-27/artifacts/azdcmpew_Logo_with_brown_background-removebg-preview.png" 
                alt="I Need Numbers" 
                style={{ height: 48, width: 'auto', marginBottom: 20, display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
              />
              <span style={{ 
                fontSize: 14, fontWeight: 700, color: '#2FA163', letterSpacing: 2,
                textTransform: 'uppercase', fontFamily: "'Playfair Display', Georgia, serif",
                display: 'block', marginBottom: 24
              }}>
                I NEED NUMBERS
              </span>
              <span style={S.badge}>60-Second Reality Check</span>
              <h1 style={S.h1}>
                Are you a <strong>real estate agent</strong> or a professional guessing machine?
              </h1>
              <p style={S.subtitle}>
                You close deals, dodge inspectors, and survive title drama &mdash; but do you
                actually know where your money goes? Let's find out.
              </p>
              <div style={{ ...S.social, justifyContent: 'center' }}>
                <div style={S.avatarRow}>
                  {['A', 'M', 'K', 'R'].map((l, i) => (
                    <div key={i} style={S.avatar(i)}>{l}</div>
                  ))}
                </div>
                <span style={S.socialText}><strong>2,400+ agents</strong> have been humbled this month</span>
              </div>
              <button
                style={S.btn}
                data-testid="quiz-start-btn"
                onClick={() => goTo(1)}
                onMouseDown={(e) => Object.assign(e.currentTarget.style, S.btnHover)}
                onMouseUp={(e) => Object.assign(e.currentTarget.style, { transform: 'scale(1)', opacity: 1 })}
                onMouseLeave={(e) => Object.assign(e.currentTarget.style, { transform: 'scale(1)', opacity: 1 })}
              >
                Roast My Business
              </button>
              <p style={S.smallText}>Free. Painless. Okay, maybe a little painful.</p>
            </div>
          </div>
        )}

        {/* ── Screens 1-5: Questions ── */}
        {qIdx && (
          <div key={animKey} style={S.fadeIn}>
            <p style={S.qNum}>QUESTION {qIdx} OF 5</p>
            <h2 style={S.qText}>{QUESTIONS[qIdx - 1].text}</h2>
            {QUESTIONS[qIdx - 1].options.map((opt) => (
              <button
                key={opt.value}
                style={S.option(selected === opt.value)}
                data-testid={`quiz-option-${opt.value}`}
                onClick={() => handleAnswer(qIdx, opt.value)}
              >
                <span style={S.optionEmoji}>{opt.emoji}</span>
                <span>{opt.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* ── Screen 6: Email Capture ── */}
        {screen === 6 && (
          <div key={animKey} style={{ ...S.fadeIn, textAlign: 'center', paddingTop: 60 }}>
            <span style={S.badge}>The moment of truth</span>
            <h2 style={{ ...S.h1, fontSize: 24 }}>
              Your score is ready. Drop your info and we'll try not to judge.
            </h2>
            <div style={{ textAlign: 'left', marginTop: 24 }}>
              <input
                style={S.input}
                placeholder="First name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="quiz-name-input"
              />
              <input
                style={{
                  ...S.input,
                  borderColor: emailError ? '#f87171' : '#1e1e36',
                }}
                placeholder="Your best email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                data-testid="quiz-email-input"
              />
              {emailError && <p style={S.errorText}>{emailError}</p>}
            </div>
            <button
              style={{ ...S.btn, marginTop: 8 }}
              data-testid="quiz-submit-btn"
              onClick={handleEmailSubmit}
            >
              Show Me The Damage
            </button>
            <button
              style={S.skipLink}
              data-testid="quiz-skip-btn"
              onClick={handleSkip}
            >
              I'm too scared to give my email
            </button>
          </div>
        )}

        {/* ── Screen 7: Results ── */}
        {screen === 7 && (
          <div key={animKey} style={{ ...S.fadeIn, paddingTop: 40 }}>
            <div style={S.scoreCircle} data-testid="quiz-result-badge">
              <span style={S.badgeEmoji}>{result.badge.emoji}</span>
              <span style={S.badgeLabel}>{result.badge.label}</span>
            </div>
            <p style={S.scoreReadout}>
              Your Score&nbsp;&middot;&nbsp;<span style={S.scoreReadoutNum}>{score}</span>/100
            </p>
            <h2 style={S.resultType} data-testid="quiz-result-type">{result.type}</h2>
            <p style={S.resultDesc}>{result.description}</p>
            <div style={S.insightCard}>
              <p style={S.insightTitle}>Your Biggest Opportunity</p>
              <p style={S.insightText}>{result.insight}</p>
            </div>
            <div style={{
              background: 'linear-gradient(135deg, #141422, #1a1a32)',
              border: '1px solid #1e1e36',
              borderRadius: 14,
              padding: '16px 20px',
              textAlign: 'center',
              margin: '24px 0'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                marginBottom: 4,
              }}>
                <img
                  src="https://customer-assets.emergentagent.com/job_agent-portal-27/artifacts/azdcmpew_Logo_with_brown_background-removebg-preview.png"
                  alt="I Need Numbers"
                  style={{ height: 26, width: 'auto' }}
                />
                <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>
                  I Need Numbers
                </div>
              </div>
              <div style={{ fontSize: 13, color: '#888' }}>
                AI-powered business coaching for real estate agents
              </div>
            </div>
            <button
              style={S.btn}
              data-testid="quiz-fix-btn"
              onClick={() => goTo(8)}
            >
              Here's How To Fix It
            </button>
            <button
              type="button"
              style={S.videoLinkBtn}
              data-testid="quiz-watch-video-btn"
              onClick={() => setVideoOpen(true)}
            >
              🎬 Watch the 45-second video
            </button>
          </div>
        )}

        {/* ── Screen 8: Benefits / CTA ── */}
        {screen === 8 && (
          <div key={animKey} style={{ ...S.fadeIn, paddingTop: 40 }}>
            <p style={{ ...S.qNum, textAlign: 'center', marginBottom: 4 }}>MEET YOUR NEW SECRET WEAPON</p>
            <h2 style={{ ...S.h1, textAlign: 'center', fontSize: 26, marginBottom: 36 }}>
              I Need Numbers
            </h2>
            {BENEFITS.map((b, i) => (
              <div key={i} style={S.benefitItem}>
                <span style={S.benefitEmoji}>{b.emoji}</span>
                <div>
                  <p style={S.benefitTitle}>{b.title}</p>
                  <p style={S.benefitDesc}>{b.desc}</p>
                </div>
              </div>
            ))}

            {/* Video teaser — above the trial CTA */}
            <div style={{ textAlign: 'center', marginTop: 24, marginBottom: 20 }}>
              <p style={{ ...S.qNum, marginBottom: 10, letterSpacing: 1.4 }}>
                TAKE A LOOK ↓
              </p>
              <INNHypeVideoThumb
                onClick={() => setVideoOpen(true)}
                width={180}
                testId="quiz-screen8-video-thumb"
              />
              <p style={{ ...S.smallText, marginTop: 10, fontSize: 12, color: '#9ca3af' }}>
                45 seconds · tap to play
              </p>
            </div>

            <a
              href="https://ineednumbers.com/auth/register"
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...S.btn, display: 'block', textAlign: 'center', textDecoration: 'none', marginTop: 12 }}
              data-testid="quiz-trial-btn"
            >
              Start Your 7-Day Free Trial
            </a>
            <p style={{ ...S.smallText, marginTop: 4 }}>No credit card required. Cancel anytime. Join agents using I Need Numbers to grow their business.</p>
          </div>
        )}
      </div>

      <div style={{
        textAlign: 'center',
        padding: '20px 0',
        fontSize: 11,
        color: '#444',
        letterSpacing: 0.5
      }}>
        Powered by <span style={{ color: '#2FA163', fontWeight: 600 }}>I Need Numbers</span>
      </div>

      <div ref={iframeRef} style={{ display: 'none' }} />

      <INNHypeVideoModal
        open={videoOpen}
        onOpenChange={setVideoOpen}
        onEnded={() => {
          setVideoOpen(false);
          window.location.href = 'https://ineednumbers.com/pricing';
        }}
      />
    </div>
  );
};

export default QuizPage;
